// The run ledger, as the Пайплайн screen needs it (04 §6, 02 §5/§6).

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { IMPLEMENTED_STAGES, type Stage, PRESETS as SHARED_PRESETS } from '@leadg/shared';
import { isConfigured, isDemoMode, supabase } from '../../lib/supabase.js';

export interface RunRow {
  id: number;
  trigger: string;
  status: string;
  stages_requested: string[] | null;
  requested_by: string | null;
  gh_run_id: string | null;
  started_at: string | null;
  finished_at: string | null;
  error: string | null;
  created_at: string;
}

export interface StageRunRow {
  run_id: number;
  stage: string;
  status: string;
  items_total: number | null;
  items_done: number | null;
  items_failed: number | null;
  items_deferred: number | null;
}

export interface FailedItemRow {
  run_id: number;
  stage: string;
  item_type: string;
  item_id: number;
  status: string;
  error_class: string | null;
  error_msg: string | null;
  attempt: number;
}

// The presets are the shared contract's, not a local copy: the local one had dropped `harvest`
// and drifted from what the runner executes.
export const PRESETS: Readonly<Record<string, readonly Stage[]>> = SHARED_PRESETS;

export function isImplemented(stage: string): boolean {
  return (IMPLEMENTED_STAGES as readonly string[]).includes(stage);
}

const DEMO_RUNS: RunRow[] = [
  {
    id: 42,
    trigger: 'manual',
    status: 'completed',
    stages_requested: ['audit', 'score', 'documents'],
    requested_by: 'laizyload@gmail.com',
    gh_run_id: '128490',
    started_at: '2026-09-08T10:00:00Z',
    finished_at: '2026-09-08T10:04:15Z',
    error: null,
    created_at: '2026-09-08T10:00:00Z',
  },
  {
    id: 41,
    trigger: 'scheduled',
    status: 'completed',
    stages_requested: ['discovery', 'details'],
    requested_by: 'cron',
    gh_run_id: '128310',
    started_at: '2026-09-07T03:00:00Z',
    finished_at: '2026-09-07T03:08:22Z',
    error: null,
    created_at: '2026-09-07T03:00:00Z',
  },
];

export function useRuns(limit = 20) {
  return useQuery({
    queryKey: ['pipeline_runs', limit],
    refetchInterval: 15_000,
    queryFn: async (): Promise<RunRow[]> => {
      if (!isConfigured || isDemoMode) {
        return DEMO_RUNS;
      }
      const { data, error } = await supabase
        .from('pipeline_runs')
        .select(
          'id, trigger, status, stages_requested, requested_by, gh_run_id, started_at, finished_at, error, created_at',
        )
        .order('id', { ascending: false })
        .limit(limit);
      if (error) throw new Error(error.message);
      return (data ?? []) as unknown as RunRow[];
    },
  });
}

export function useStageRuns(runId: number | null) {
  return useQuery({
    queryKey: ['pipeline_stage_runs', runId],
    enabled: runId !== null,
    refetchInterval: 15_000,
    queryFn: async (): Promise<StageRunRow[]> => {
      if (!isConfigured || isDemoMode) {
        return [
          {
            run_id: runId ?? 42,
            stage: 'audit',
            status: 'completed',
            items_total: 12,
            items_done: 12,
            items_failed: 0,
            items_deferred: 0,
          },
          {
            run_id: runId ?? 42,
            stage: 'score',
            status: 'completed',
            items_total: 12,
            items_done: 12,
            items_failed: 0,
            items_deferred: 0,
          },
          {
            run_id: runId ?? 42,
            stage: 'documents',
            status: 'completed',
            items_total: 12,
            items_done: 12,
            items_failed: 0,
            items_deferred: 0,
          },
        ];
      }
      const { data, error } = await supabase
        .from('pipeline_stage_runs')
        .select('run_id, stage, status, items_total, items_done, items_failed, items_deferred')
        .eq('run_id', runId!);
      if (error) throw new Error(error.message);
      return (data ?? []) as unknown as StageRunRow[];
    },
  });
}

export function useFailedItems(runId: number | null) {
  return useQuery({
    queryKey: ['pipeline_items_failed', runId],
    enabled: runId !== null,
    queryFn: async (): Promise<FailedItemRow[]> => {
      if (!isConfigured || isDemoMode) {
        return [];
      }
      const { data, error } = await supabase
        .from('pipeline_items')
        .select('run_id, stage, item_type, item_id, status, error_class, error_msg, attempt')
        .eq('run_id', runId!)
        .neq('status', 'ok')
        .limit(200);
      if (error) throw new Error(error.message);
      return (data ?? []) as unknown as FailedItemRow[];
    },
  });
}

async function extractFunctionError(error: Error): Promise<string> {
  const ctx = (error as { context?: { json?: () => Promise<{ error?: string }> } }).context;
  if (!ctx || typeof ctx.json !== 'function') return error.message;
  try {
    const body = await ctx.json();
    if (body && typeof body.error === 'string' && body.error.length > 0) {
      return body.error;
    }
  } catch {
    // Fall back to the top-level error message
  }
  return error.message;
}

export function useDispatchRun() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async ({ stages, leadId }: { stages: readonly Stage[]; leadId?: number }) => {
      if (!isConfigured || isDemoMode) {
        return 43;
      }
      const { data, error } = await supabase.functions.invoke<{ runId: number; error?: string }>(
        'dispatch-pipeline',
        {
          body: {
            environment: 'prod',
            stages,
            dispatch_uuid: crypto.randomUUID(),
            ...(leadId === undefined ? {} : { scope: { leadId } }),
          },
        },
      );

      if (error) {
        throw new Error(await extractFunctionError(error));
      }
      if (!data || typeof data.runId !== 'number') {
        throw new Error(data?.error ?? 'Пускането не върна номер');
      }
      return data.runId;
    },
    onSuccess: () => void client.invalidateQueries({ queryKey: ['pipeline_runs'] }),
  });
}

export function useCancelRun() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (runId: number) => {
      if (!isConfigured || isDemoMode) return;
      const { error } = await supabase.rpc('request_run_cancel', { p_run_id: runId });
      if (error) throw new Error(error.message);
    },
    onSuccess: () => void client.invalidateQueries({ queryKey: ['pipeline_runs'] }),
  });
}

export interface Backlog {
  leads: number;
  unaudited: number;
  unscored: number;
  undocumented: number;
}

export function useBacklog() {
  return useQuery({
    queryKey: ['backlog'],
    refetchInterval: 30_000,
    queryFn: async (): Promise<Backlog> => {
      if (!isConfigured || isDemoMode) {
        return { leads: 124, unaudited: 18, unscored: 12, undocumented: 9 };
      }
      const base = () =>
        supabase
          .from('leads')
          .select('id', { count: 'exact', head: true })
          .is('parent_lead_id', null)
          .eq('do_not_contact', false)
          .not('business_status', 'in', '("CLOSED_PERMANENTLY","REMOVED_FROM_GOOGLE")');

      const [leads, unaudited, unscored, undocumented] = await Promise.all([
        base(),
        base().is('website_url', null),
        base().is('lead_score', null),
        base().is('tier', null),
      ]);

      return {
        leads: leads.count ?? 0,
        unaudited: unaudited.count ?? 0,
        unscored: unscored.count ?? 0,
        undocumented: undocumented.count ?? 0,
      };
    },
  });
}
